import { where } from 'firebase/firestore';
import { firestoreWrapper } from './firestoreWrapper';
import { AVAILABLE_APPS } from './appAccess';

export interface AllowedEmail {
  email: string;
  displayName?: string;
  isAdmin: boolean;
  addedAt: Date;
  isManuallyAdded?: boolean;
  source?: 'firebase' | 'sheets';
}

// Cache configuration
const CACHE_TTL = 30 * 1000; // 30 seconds - much shorter for immediate updates
const ALLOWED_EMAILS_CACHE_KEY = 'allowedEmailsCache';

interface CacheEntry {
  data: AllowedEmail[];
  timestamp: number;
}

// In-memory cache
let memoryCache: CacheEntry | null = null;

export const getAllowedEmails = async (): Promise<AllowedEmail[]> => {
  try {
    // Check memory cache first
    const now = Date.now();
    if (memoryCache && now - memoryCache.timestamp < CACHE_TTL) {
      
      return memoryCache.data;
    }

    // Check localStorage cache
    const cachedData = localStorage.getItem(ALLOWED_EMAILS_CACHE_KEY);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData) as CacheEntry;
        if (now - parsed.timestamp < CACHE_TTL) {
          
          memoryCache = parsed;
          return parsed.data;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    
    const emailsRef = firestoreWrapper.collection('allowedEmails');
    const querySnapshot = await firestoreWrapper.getDocs(emailsRef);
    
    const emails = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        email: data.email,
        displayName: data.displayName || '',
        isAdmin: data.isAdmin || false,
        addedAt: data.addedAt?.toDate() || new Date(),
        isManuallyAdded: data.isManuallyAdded || false,
        source: 'firebase' as const
      };
    });

    // Cache the result
    const cacheEntry: CacheEntry = {
      data: emails,
      timestamp: now
    };
    memoryCache = cacheEntry;
    localStorage.setItem(ALLOWED_EMAILS_CACHE_KEY, JSON.stringify(cacheEntry));
    
    return emails;
  } catch (error) {
    
    throw error;
  }
};

export const addAllowedEmail = async (
  email: string,
  displayName: string,
  isManuallyAdded: boolean = true,
  isAdmin: boolean = false
): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    
    // Use email as document ID for consistent access
    const emailsRef = firestoreWrapper.doc(
      firestoreWrapper.collection('allowedEmails'),
      normalizedEmail
    );
    
    // Use setDoc with merge to update existing or create new
    await firestoreWrapper.setDoc(emailsRef, {
      email: normalizedEmail,
      displayName,
      isAdmin,
      addedAt: new Date(),
      isManuallyAdded,
      updatedAt: new Date()
    }, { merge: true });

    // Create app access document with no apps by default
    // App access should be granted separately through the admin panel
    const appAccessRef = firestoreWrapper.doc(
      firestoreWrapper.collection('userAppAccess'),
      normalizedEmail
    );
    await firestoreWrapper.setDoc(appAccessRef, {
      allowedApps: [] // No apps by default - must be granted through admin panel
    }, { merge: true });

    // Clear cache
    clearCache();
  } catch (error) {
    
    throw error;
  }
};

export const removeAllowedEmail = async (email: string): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    
    // Use email as document ID for direct access
    const emailDocRef = firestoreWrapper.doc(
      firestoreWrapper.collection('allowedEmails'),
      normalizedEmail
    );
    
    const docSnapshot = await firestoreWrapper.getDoc(emailDocRef);
    
    if (!docSnapshot.exists()) {
      throw new Error('Email not found');
    }

    // Check if the document is manually added
    const docData = docSnapshot.data();
    if (!docData.isManuallyAdded) {
      throw new Error('Cannot remove non-manually added email');
    }

    // Delete the document and the app access document
    await Promise.all([
      firestoreWrapper.deleteDoc(emailDocRef),
      firestoreWrapper.deleteDoc(
        firestoreWrapper.doc(
          firestoreWrapper.collection('userAppAccess'),
          normalizedEmail
        )
      )
    ]);

    // Clear cache
    clearCache();
  } catch (error) {
    
    throw error;
  }
};

export const updateDisplayName = async (email: string, displayName: string): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    
    // Use email as document ID for direct access
    const emailDocRef = firestoreWrapper.doc(
      firestoreWrapper.collection('allowedEmails'),
      normalizedEmail
    );
    
    const docSnapshot = await firestoreWrapper.getDoc(emailDocRef);
    
    if (!docSnapshot.exists()) {
      throw new Error('Email not found');
    }

    // Update the document with new display name
    await firestoreWrapper.updateDoc(emailDocRef, {
      displayName: displayName,
      updatedAt: new Date()
    });
    
    // Clear cache
    clearCache();
  } catch (error) {
    
    console.error('Full error:', JSON.stringify(error));
    throw error;
  }
};

export const toggleAdminStatus = async (email: string): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    
    // Use email as document ID for direct access
    const emailDocRef = firestoreWrapper.doc(
      firestoreWrapper.collection('allowedEmails'),
      normalizedEmail
    );
    
    const docSnapshot = await firestoreWrapper.getDoc(emailDocRef);
    
    if (!docSnapshot.exists()) {
      throw new Error('Email not found');
    }

    // Get current admin status and toggle it
    const currentData = docSnapshot.data();
    const newIsAdmin = !currentData.isAdmin;
    
    // Update the document with new admin status
    await firestoreWrapper.updateDoc(emailDocRef, {
      isAdmin: newIsAdmin,
      updatedAt: new Date()
    });
    
    // Note: We do NOT automatically change app access when toggling admin status
    // App access should be managed separately through the App Access controls

    // Clear cache
    clearCache();
    
    // Clear all authentication caches and broadcast to other tabs
    try {
      const { clearAllAuthCaches, broadcastCacheInvalidation } = await import('./cacheInvalidation');
      await clearAllAuthCaches();
      broadcastCacheInvalidation();
    } catch (error) {
      
    }
  } catch (error) {
    
    throw error;
  }
};

// Clear cache
export const clearCache = () => {
  memoryCache = null;
  localStorage.removeItem(ALLOWED_EMAILS_CACHE_KEY);
};
